import { useState } from "react";
import { CalendarCheck, Zap, GripVertical, Clock, Reply } from "lucide-react";
import { STAGES, fmtMoney } from "../lib/stages";

const OPEN_STAGES = ["new_lead", "call_scheduled", "follow_up_book", "follow_up_close"];
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

function Card({ p, onOpen, onDragStart, onDragEnd, dragging }) {
  const days = OPEN_STAGES.includes(p.stage) ? daysSince(p.stage_changed_at || p.updated_at || p.created_at) : null;
  const stall = days != null && days >= 5 ? (days >= 10 ? "rose" : "amber") : null;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, p)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(p)}
      data-testid={`kanban-card-${p.id}`}
      className={`group rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-3 cursor-pointer transition-colors ${dragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-white text-sm truncate">{p.name || "—"}</p>
          <p className="text-[11px] text-slate-500 truncate">{p.company || p.email}</p>
        </div>
        <GripVertical size={14} className="text-slate-600 group-hover:text-slate-400 shrink-0 cursor-grab" />
      </div>
      {p.call_time && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-[#9cc6f0]">
          {p.self_scheduled ? <Zap size={11} className="text-amber-400" /> : <CalendarCheck size={11} />}
          {new Date(p.call_time).toLocaleDateString([], { month: "short", day: "numeric" })}
        </p>
      )}
      {(p.contacted || p.had_call || p.no_show || p.reply_received) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {p.reply_received && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-rose-500/20 text-rose-200 flex items-center gap-0.5"><Reply size={9} /> Replied</span>}
          {p.contacted && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-500/15 text-sky-300">Contacted</span>}
          {p.had_call && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-teal-500/15 text-teal-300">Had Call</span>}
          {p.no_show && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-rose-500/15 text-rose-300">No-show</span>}
        </div>
      )}
      {stall && (
        <p className={`mt-1.5 flex items-center gap-1 text-[10px] font-mono-tech ${stall === "rose" ? "text-rose-400" : "text-amber-400"}`} data-testid={`kanban-stall-${p.id}`}>
          <Clock size={10} /> {days}d in stage{stall === "rose" ? " · going cold" : ""}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[9px] font-mono-tech uppercase tracking-wider text-slate-500">{p.source}</span>
        {(p.monthly_value || 0) > 0 && <span className="text-xs font-mono-tech text-emerald-300/90">{fmtMoney(p.monthly_value)}/mo</span>}
      </div>
    </div>
  );
}

export default function KanbanBoard({ prospects, onOpen, onMove }) {
  const [dragId, setDragId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  const onDragStart = (e, p) => {
    setDragId(p.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", p.id);
  };
  const onDragEnd = () => { setDragId(null); setOverStage(null); };
  const onDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setOverStage(null);
    setDragId(null);
    const p = prospects.find((x) => x.id === id);
    if (p && p.stage !== stage) onMove(p, stage);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 fade-up" data-testid="kanban-board">
      {STAGES.map((s) => {
        const cards = prospects.filter((p) => p.stage === s.value);
        const total = cards.reduce((a, p) => a + (p.monthly_value || 0), 0);
        return (
          <div
            key={s.value}
            data-testid={`kanban-column-${s.value}`}
            onDragOver={(e) => { e.preventDefault(); setOverStage(s.value); }}
            onDragLeave={() => setOverStage((cur) => (cur === s.value ? null : cur))}
            onDrop={(e) => onDrop(e, s.value)}
            className={`shrink-0 w-72 rounded-xl border p-3 transition-colors ${overStage === s.value ? "border-[#4690d9] bg-[#4690d9]/[0.06]" : "border-white/10 bg-black/20"}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                <p className="text-xs font-semibold text-white">{s.label}</p>
                <span className="text-[10px] font-mono-tech text-slate-500">{cards.length}</span>
              </div>
              {total > 0 && <span className="text-[10px] font-mono-tech text-emerald-300/80">{fmtMoney(total)}/mo</span>}
            </div>
            <div className="space-y-2 min-h-[80px]">
              {cards.map((p) => (
                <Card key={p.id} p={p} onOpen={onOpen} onDragStart={onDragStart} onDragEnd={onDragEnd} dragging={dragId === p.id} />
              ))}
              {cards.length === 0 && (
                <p className={`text-[11px] text-center py-6 rounded-lg border border-dashed ${overStage === s.value ? "border-[#4690d9]/50 text-[#9cc6f0]" : "border-white/5 text-slate-600"}`}>
                  Drop here
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
